import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  displayPair,
  followerCount,
  formatAmount,
  masterImage,
  pnlClass,
  pnlUsdt,
  statusClass,
  usdt,
} from "./helpers";
import MasterRating from "./MasterRating";
import "./copyTrading.css";

const HISTORY_LIMIT = 20;
const TABS = [
  { id: "masters", label: "Masters", public: true },
  { id: "following", label: "My Following" },
  { id: "history", label: "Trade History" },
  { id: "pnl", label: "PnL Dashboard" },
];

function CopyTrading() {
  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("masters");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState("");

  const [masters, setMasters] = useState([]);
  const [following, setFollowing] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySkip, setHistorySkip] = useState(0);
  const [historyStatus, setHistoryStatus] = useState("");
  const [historyMasterId, setHistoryMasterId] = useState("");
  const [pnl, setPnl] = useState({ totalPnl: 0, perMaster: [] });

  const followingIds = useMemo(
    () => new Set((following || []).map((item) => item?.master_id).filter(Boolean)),
    [following]
  );

  const visibleTabs = useMemo(
    () => (token ? TABS : TABS.filter((tab) => tab.public)),
    [token]
  );

  useEffect(() => {
    if (!token && activeTab !== "masters") setActiveTab("masters");
  }, [token, activeTab]);

  const requireLogin = () => {
    navigate("/login", { replace: false, state: { redirectTo: location.pathname } });
  };

  const loadMasters = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.copyTradingMasters();
      if (result?.success) {
        setMasters(Array.isArray(result?.data) ? result.data : []);
      } else {
        setMasters([]);
        if (result?.message) alertErrorMessage(result.message);
      }
    } catch (error) {
      setMasters([]);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const loadFollowing = async (silent = false) => {
    if (!token) {
      setFollowing([]);
      return;
    }
    if (!silent) LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.copyTradingMyFollowing();
      if (result?.success) {
        setFollowing(Array.isArray(result?.data) ? result.data : []);
      } else {
        setFollowing([]);
        if (!silent && result?.message) alertErrorMessage(result.message);
      }
    } catch (error) {
      setFollowing([]);
    } finally {
      if (!silent) LoaderHelper.loaderStatus(false);
    }
  };

  const loadHistory = useCallback(async (skip = 0, status = historyStatus, silent = false, masterId = historyMasterId) => {
    if (!token) {
      setHistory([]);
      setHistoryTotal(0);
      return;
    }
    if (!silent) LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.copyTradingHistory({
        skip,
        limit: HISTORY_LIMIT,
        status: status || undefined,
        master_id: masterId || undefined,
      });
      if (result?.success) {
        setHistory(Array.isArray(result?.data) ? result.data : []);
        setHistoryTotal(result?.total || 0);
        setHistorySkip(skip);
      } else {
        setHistory([]);
        setHistoryTotal(0);
        if (!silent && result?.message) alertErrorMessage(result.message);
      }
    } catch (error) {
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      if (!silent) LoaderHelper.loaderStatus(false);
    }
  }, [historyMasterId, historyStatus, token]);

  const loadPnl = async () => {
    if (!token) {
      setPnl({ totalPnl: 0, perMaster: [] });
      return;
    }
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.copyTradingPnl();
      if (result?.success) {
        setPnl(result?.data || { totalPnl: 0, perMaster: [] });
      } else {
        setPnl({ totalPnl: 0, perMaster: [] });
        if (result?.message) alertErrorMessage(result.message);
      }
    } catch (error) {
      setPnl({ totalPnl: 0, perMaster: [] });
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  useEffect(() => {
    loadMasters();
    if (token) loadFollowing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (activeTab === "following") loadFollowing();
    if (activeTab === "history") {
      loadFollowing(true);
      loadHistory(0, historyStatus, false, historyMasterId);
    }
    if (activeTab === "pnl") loadPnl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "history" || !token) return undefined;
    const refresh = () => loadHistory(historySkip, historyStatus, true, historyMasterId);
    const quick = setTimeout(refresh, 1200);
    const interval = setInterval(refresh, 2000);
    return () => {
      clearTimeout(quick);
      clearInterval(interval);
    };
  }, [activeTab, historyMasterId, historySkip, historyStatus, loadHistory, token]);

  const historyMasters = useMemo(() => {
    const map = new Map();
    following.forEach((item) => {
      if (item?.master_id) map.set(item.master_id, item?.name || "Master");
    });
    history.forEach((item) => {
      if (item?.master_id) map.set(item.master_id, item?.masterName || "Master");
    });
    return Array.from(map.entries());
  }, [following, history]);

  const filteredMasters = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return masters;
    return masters.filter((item) =>
      [item?.name, item?.bio, item?.short_name, item?.currency, displayPair(item)]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [masters, search]);

  const handleFollowToggle = async (masterId, isFollowing) => {
    if (!token) {
      requireLogin();
      return;
    }
    setBusyId(masterId);
    try {
      const result = isFollowing
        ? await AuthService.copyTradingUnfollow(masterId)
        : await AuthService.copyTradingFollow(masterId);
      if (result?.success) {
        alertSuccessMessage(result.message);
        await loadFollowing(true);
        if (activeTab === "following") await loadFollowing();
      } else {
        alertErrorMessage(result?.message);
      }
    } catch (error) {
      alertErrorMessage("Unable to update follow status.");
    } finally {
      setBusyId("");
    }
  };

  const handleTab = (tabId) => {
    setActiveTab(tabId);
  };

  const renderEmpty = (message) => (
    <div className="no_data_outer">
      <div className="no_data_vector">
        <img src="/images/no_data_vector.svg" alt="no-data" />
      </div>
      <p>{message}</p>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Copy Trading – Follow Masters on Vorienx</title>
        <meta
          name="description"
          content="Copy professional traders on Vorienx. Browse masters, follow strategies, and track your copy trading PnL from your spot wallet."
        />
      </Helmet>

      <section className="earning_outer_s ct-page">
        <div className="container">
          <div className="ct-hero">
            <span className="ct-kicker">Copy Trading</span>
            <h1>Follow masters.<br />Copy trades in USDT.</h1>
            <p>Browse professional traders and view their profiles. Login to follow and copy trades from your spot wallet.</p>
          </div>
          <div className="earning_section_cate">
            {token && (
            <div className="row align-items-center ct-page-head">
              <div className="col-12">
                <div className="earning_right_tab">
                  <div className="responsive-table">
                    <ul className="nav nav-tabs" id="myTab" role="tablist">
                      {visibleTabs.map((tab) => (
                        <li className="nav-item" key={tab.id}>
                          <button
                            type="button"
                            className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                            onClick={() => handleTab(tab.id)}
                          >
                            {tab.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            )}

            <div className="tab-content">
              {activeTab === "masters" && (
                <div className="tab-pane show active">
                  <div className="ct-toolbar">
                    <div className="ct-notice">
                      <span className="ct-notice-dot" />
                      <p>Funds are taken from your spot USDT wallet. Pair shows BTC/USDT; profit and loss settle in USDT.</p>
                    </div>
                    <form className="ct-search" onSubmit={(e) => e.preventDefault()}>
                      <i className="ri-search-line" aria-hidden="true"></i>
                      <input
                        type="text"
                        placeholder="Search masters"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </form>
                  </div>

                  {filteredMasters.length > 0 ? (
                    <div className="ct-master-grid">
                      {filteredMasters.map((master) => {
                        const isFollowing = followingIds.has(master?._id);
                        return (
                          <div className={`ct-master-card ${isFollowing ? "is-following" : ""}`} key={master?._id}>
                            <div className="ct-master-card-head">
                              <div className="ct-avatar-wrap">
                                <img
                                  className="ct-avatar"
                                  src={masterImage(master?.profileImage)}
                                  alt={master?.name}
                                  onError={(e) => { e.currentTarget.src = "/images/user_icon.svg"; }}
                                />
                              </div>
                              <div className="ct-master-copy">
                                <div className="ct-name-row">
                                  <h3 className="ct-master-name">{master?.name}</h3>
                                  <span className="ct-chip pair">{displayPair(master)}</span>
                                  {isFollowing && <span className="ct-chip following">Following</span>}
                                </div>
                                <MasterRating rating={master?.rating} />
                                <p className="ct-master-bio">{master?.bio || "Professional copy trading master"}</p>
                              </div>
                              {master?.displayWinRate != null && (
                                <span className="ct-win-badge">{formatAmount(master.displayWinRate, 0)}% Win</span>
                              )}
                            </div>

                            <div className="ct-stats">
                              <div className="ct-stat">
                                <label>Followers</label>
                                <span>{formatAmount(followerCount(master), 0)}</span>
                              </div>
                              <div className="ct-stat">
                                <label>Funds Managed</label>
                                <span>{usdt(master?.aum)}</span>
                              </div>
                              <div className="ct-stat profit">
                                <label>Total Profit Given</label>
                                <span className="ct-pnl-positive">{usdt(master?.totalProfitGiven)}</span>
                              </div>
                              <div className="ct-stat loss">
                                <label>Total Loss</label>
                                <span className="ct-pnl-negative">{usdt(master?.totalLossGiven)}</span>
                              </div>
                            </div>

                            <div className="ct-alloc">
                              <div className="ct-alloc-item">
                                <label>Uses of your balance</label>
                                <strong>{master?.useBalancePercent || 0}%</strong>
                              </div>
                              {master?.profitPercent != null && (
                                <div className="ct-alloc-item gold">
                                  <label>Up to profit</label>
                                  <strong>{master.profitPercent}%</strong>
                                </div>
                              )}
                            </div>

                            <div className="ct-card-actions">
                              <Link className="ct-btn light" to={`/copy-trading/${master?._id}`}>View Profile</Link>
                              {token ? (
                                <button
                                  type="button"
                                  className={`ct-btn ${isFollowing ? "danger" : "primary"}`}
                                  disabled={busyId === master?._id}
                                  onClick={() => handleFollowToggle(master?._id, isFollowing)}
                                >
                                  {isFollowing ? "Unfollow" : "Follow"}
                                </button>
                              ) : (
                                <Link
                                  className="ct-btn primary"
                                  to="/login"
                                  state={{ redirectTo: `/copy-trading/${master?._id}` }}
                                >
                                  Login to Follow
                                </Link>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : renderEmpty("No masters found")}
                </div>
              )}

              {activeTab === "following" && (
                <div className="tab-pane show active">
                  {!token ? (
                    <div className="ct-login-prompt">
                      {renderEmpty(
                        <>Please <Link to="/login" state={{ redirectTo: "/copy-trading" }}>Login</Link> to continue</>
                      )}
                    </div>
                  ) : following.length > 0 ? (
                    <div className="ct-follow-grid">
                      {following.map((item) => (
                        <div className="ct-follow-card" key={item?.master_id}>
                          <div className="ct-avatar-wrap">
                            <img
                              className="ct-avatar"
                              src={masterImage(item?.profileImage)}
                              alt={item?.name}
                              onError={(e) => { e.currentTarget.src = "/images/user_icon.svg"; }}
                            />
                          </div>
                          <div className="ct-follow-meta">
                            <div className="ct-name-row">
                              <h5>{item?.name}</h5>
                              <span className="ct-chip following">Following</span>
                            </div>
                            <p>
                              Uses {item?.useBalancePercent || 0}% of your balance
                              {item?.profitPercent != null ? ` · Up to ${item.profitPercent}% profit` : ""}
                            </p>
                          </div>
                          <div className={`ct-follow-pnl ${pnlClass(item?.totalPnl)}`}>
                            <label>My PnL</label>
                            <strong>{pnlUsdt(item?.totalPnl)}</strong>
                          </div>
                          <div className="ct-follow-actions">
                            <Link className="ct-btn light" to={`/copy-trading/${item?.master_id}`}>View</Link>
                            <button
                              type="button"
                              className="ct-btn danger"
                              disabled={busyId === item?.master_id}
                              onClick={() => handleFollowToggle(item?.master_id, true)}
                            >
                              Unfollow
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : renderEmpty("You are not following any masters yet")}
                </div>
              )}

              {activeTab === "history" && (
                <div className="tab-pane show active">
                  {!token ? (
                    renderEmpty(
                      <>Please <Link to="/login" state={{ redirectTo: "/copy-trading" }}>Login</Link> to continue</>
                    )
                  ) : (
                    <>
                      <div className="ct-filters">
                        <select
                          value={historyStatus}
                          onChange={(e) => {
                            const next = e.target.value;
                            setHistoryStatus(next);
                            loadHistory(0, next, false, historyMasterId);
                          }}
                        >
                          <option value="">All status</option>
                          <option value="OPEN">OPEN</option>
                          <option value="EXECUTED">EXECUTED</option>
                          <option value="CANCELLED">CANCELLED</option>
                        </select>
                        <select
                          value={historyMasterId}
                          onChange={(e) => {
                            const next = e.target.value;
                            setHistoryMasterId(next);
                            loadHistory(0, historyStatus, false, next);
                          }}
                        >
                          <option value="">All masters</option>
                          {historyMasters.map(([id, name]) => (
                            <option value={id} key={id}>{name}</option>
                          ))}
                        </select>
                        <div className="ct-notice">
                          <span className="ct-notice-dot" />
                          <p>Funds are taken from your spot USDT wallet. Pair shows BTC/USDT; PnL settles in USDT.</p>
                        </div>
                      </div>
                      <div className="dashboard_recent_s ct-table-card">
                        <div className="table-responsive recenttable_s">
                          <table>
                            <thead>
                              <tr>
                                <th>Master Name</th>
                                <th>Pair</th>
                                <th>Side</th>
                                <th>Price</th>
                                <th>Locked Amount</th>
                                <th>Status</th>
                                <th>PnL</th>
                                <th>Return</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {history.length > 0 ? history.map((item) => (
                                <tr key={item?._id}>
                                  <td>{item?.masterName || "—"}</td>
                                  <td>{displayPair(item)}</td>
                                  <td className={`ct-side ${String(item?.side || "").toLowerCase()}`}>{item?.side || "—"}</td>
                                  <td>{formatAmount(item?.price)}</td>
                                  <td>{usdt(item?.lockedAmount)}</td>
                                  <td>
                                    <span className={`ct-status ${statusClass(item?.status)}`}>
                                      {item?.status || "—"}
                                    </span>
                                  </td>
                                  <td className={pnlClass(item?.pnlAmount)}>{pnlUsdt(item?.pnlAmount)}</td>
                                  <td>{usdt(item?.returnAmount)}</td>
                                  <td>{item?.createdAt ? moment(item.createdAt).format("DD/MM/YYYY hh:mm A") : "—"}</td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan="9">{renderEmpty("No copy trades found")}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {historyTotal > HISTORY_LIMIT && (
                          <div className="ct-pagination">
                            <span>
                              {historySkip + 1}-{Math.min(historySkip + HISTORY_LIMIT, historyTotal)} of {historyTotal}
                            </span>
                            <button type="button" disabled={historySkip <= 0} onClick={() => loadHistory(Math.max(historySkip - HISTORY_LIMIT, 0), historyStatus, false, historyMasterId)}>Prev</button>
                            <button type="button" disabled={historySkip + HISTORY_LIMIT >= historyTotal} onClick={() => loadHistory(historySkip + HISTORY_LIMIT, historyStatus, false, historyMasterId)}>Next</button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "pnl" && (
                <div className="tab-pane show active">
                  {!token ? (
                    renderEmpty(
                      <>Please <Link to="/login" state={{ redirectTo: "/copy-trading" }}>Login</Link> to continue</>
                    )
                  ) : (
                    <>
                      <div className="ct-pnl-hero">
                        <label>Total PnL</label>
                        <h2 className={pnlClass(pnl?.totalPnl)}>{pnlUsdt(pnl?.totalPnl)}</h2>
                        <p className="ct-page-sub">Settled in USDT from your spot wallet</p>
                      </div>
                      <div className="dashboard_recent_s ct-table-card">
                        <h4 className="ct-section-title">PnL by Master</h4>
                        <div className="table-responsive recenttable_s">
                          <table>
                            <thead>
                              <tr>
                                <th>Master</th>
                                <th>Status</th>
                                <th>Total PnL</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(pnl?.perMaster || []).length > 0 ? pnl.perMaster.map((item) => (
                                <tr
                                  key={item?.master_id}
                                  className="ct-clickable"
                                  onClick={() => item?.master_id && navigate(`/copy-trading/${item.master_id}`)}
                                >
                                  <td>{item?.masterName || "—"}</td>
                                  <td>
                                    <span className={`ct-chip ${item?.isFollowing ? "following" : ""}`}>
                                      {item?.isFollowing ? "Following" : "Not following"}
                                    </span>
                                  </td>
                                  <td className={pnlClass(item?.totalPnl)}>{pnlUsdt(item?.totalPnl)}</td>
                                </tr>
                              )) : (
                                <tr>
                                  <td colSpan="3">{renderEmpty("No PnL data yet")}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default CopyTrading;
